<script context="module">
	// This theme's static grid wrapper must also be used while editing.
	export const preserveEditorLayout = true;

	// Classes offered in the admin BlockContainer config; the engine adds them to its inner
	// div as `blockContainer-<class>`, this wrapper mirrors them as `af-bc-<class>`.
	export const cssClasses = [
		"header",
		"site",
		"wide",
		"bento",
		"span-4",
		"span-6",
		"span-8",
		"span-12",
		"section",
		"panel",
		"band-sunk",
		"gap-tight",
		"article",
	];
</script>

<script>
	// Layout engine. The engine renders <Default {...$$props}><div class="blockContainer
	// flex-{layout} blockContainer-co-{N} …">children</div></Default>; the grid lives on
	// that inner div, driven by the admin-configured column count.
	export let data = {};
	export let id;
	export let globalId = null;
	export let globalSource = null;
	export let parentGlobalSource = null;
	export let components = [];
	export let type = "BlockContainer";
	id || globalId || globalSource || parentGlobalSource || components || type;

	$: classes = ((data && data.cssClasses) || [])
		.filter((c) => typeof c === "string" && c.length)
		.map((c) => `af-bc-${c}`)
		.join(" ");

	$: cols = (data && data.columns) || 1;
</script>

<div class="af-bc {classes}" style="--af-cols: {cols}">
	<slot />
</div>

<style lang="scss">
	@use "../../styles/tokens" as t;

	:global {
		/* admin page-builder preview shim: main.svelte (where tokens live) is never bundled
		   into the admin, so the light token set is re-emitted under the builder root */
		.page-builder {
			@include t.af-tokens-static;
			@include t.af-tokens-light;
		}

		.af-bc {
			min-width: 0;
		}

		/* neutralise the engine's percentage widths and flex defaults */
		.af-bc > .blockContainer[class*="blockContainer-co-"] {
			width: auto;
		}

		.af-bc > .blockContainer.flex-column {
			display: flex;
			flex-direction: column;
			gap: var(--af-space-6);
		}

		.af-bc > .blockContainer.flex-row {
			display: grid;
			grid-template-columns: repeat(var(--af-cols, 1), minmax(0, 1fr));
			gap: var(--af-bento-gutter);
			align-items: stretch;
			justify-content: stretch;
		}

		.af-bc-gap-tight > .blockContainer {
			gap: var(--af-space-4);
		}

		.af-bc-header > .blockContainer.flex-column {
			gap: 0;
		}

		.af-bc-site {
			width: 100%;
			max-width: var(--af-site-max);
			margin-inline: auto;
			padding: var(--af-bento-gutter) var(--af-gutter) var(--af-space-12);
		}

		/* modules inside the site column are separated by the bento gutter, like the cards */
		.af-bc-site > .blockContainer.flex-column {
			gap: var(--af-bento-gutter);
		}

		/* sunk panel for hand-composed modules (Title + list) */
		.af-bc-panel > .blockContainer {
			padding: var(--af-space-4);
			background: var(--af-surface-sunk);
			border-radius: var(--af-radius-card);
		}

		.af-bc-panel > .blockContainer.flex-column {
			gap: var(--af-space-2);
		}

		.af-bc-wide {
			width: 100%;
			max-width: var(--af-container-max);
			margin-inline: auto;
		}

		.af-bc-section {
			padding-block: var(--af-space-8);
		}

		.af-bc-band-sunk {
			background: var(--af-surface-sunk);
			border-block: 1px solid var(--af-hairline);
		}

		/* bento: 12-column grid; nested column containers carry a span class */
		.af-bc-bento > .blockContainer.flex-row {
			grid-template-columns: repeat(12, minmax(0, 1fr));
			grid-auto-rows: minmax(var(--af-bento-row), auto);
		}

		.af-bc-span-4 {
			grid-column: span 4;
		}

		.af-bc-span-6 {
			grid-column: span 6;
		}

		.af-bc-span-8 {
			grid-column: span 8;
		}

		.af-bc-span-12 {
			grid-column: 1 / -1;
		}

		/* cells stretch so the hero can fill two bento rows */
		.af-bc-bento .af-bc > .blockContainer {
			height: 100%;
		}

		@media (min-width: 768px) {
			.af-bc-section {
				padding-block: var(--af-space-12);
			}
		}

		@media (min-width: 768px) and (max-width: 1023px) {
			.af-bc-span-4 {
				grid-column: span 6;
			}

			.af-bc-span-8 {
				grid-column: 1 / -1;
			}
		}

		@media (max-width: 767px) {
			.af-bc > .blockContainer.flex-row,
			.af-bc-bento > .blockContainer.flex-row {
				grid-template-columns: minmax(0, 1fr);
			}

			.af-bc[class*="af-bc-span-"] {
				grid-column: 1 / -1;
			}
		}
	}
</style>
